import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Coins, Copy, Check, Upload, Loader2, AlertTriangle, Clock, CheckCircle2, XCircle, ArrowLeft, Wallet, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuyCreditsPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [txnId, setTxnId] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(0);
  const fileRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, subsRes] = await Promise.all([
        axios.get(`${API}/billing/payment-config`),
        axios.get(`${API}/billing/payment-submissions`),
      ]);
      setConfig(configRes.data);
      setSubmissions(subsRes.data.submissions || []);
    } catch (e) {
      toast.error("Failed to load payment config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCopyAddress = () => {
    if (config?.wallet_address) {
      navigator.clipboard.writeText(config.wallet_address);
      setCopied(true);
      toast.success("Wallet address copied!");
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Only image files are allowed"); return; }
    setScreenshot(file);
  };

  const handleSubmitPayment = async () => {
    if (!txnId.trim() && !screenshot) {
      toast.error("Please provide a TXN ID or upload a screenshot");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("package_index", selectedPackage);
      if (txnId.trim()) formData.append("txn_id", txnId.trim());
      if (screenshot) formData.append("screenshot", screenshot);
      const res = await axios.post(`${API}/billing/submit-payment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      setShowForm(false);
      setTxnId("");
      setScreenshot(null);
      const subsRes = await axios.get(`${API}/billing/payment-submissions`);
      setSubmissions(subsRes.data.submissions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: { bg: "bg-amber-500/10 text-amber-400 border-amber-500/20", icon: <Clock className="h-3 w-3" />, label: "Pending" },
      approved: { bg: "bg-green-500/10 text-green-400 border-green-500/20", icon: <CheckCircle2 className="h-3 w-3" />, label: "Approved" },
      rejected: { bg: "bg-red-500/10 text-red-400 border-red-500/20", icon: <XCircle className="h-3 w-3" />, label: "Rejected" },
    };
    const s = map[status];
    return s ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.bg}`}>{s.icon} {s.label}</span> : null;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  }

  const currentPkg = config?.packages?.[selectedPackage] || { minutes: 2500, price_usd: 50, label: "Starter", bonus: 0 };
  const packages = config?.packages || [];

  const steps = [
    { num: 1, text: "Choose a minutes package below" },
    { num: 2, text: `Send exactly ${currentPkg.price_usd} USDT (TRC20) to the wallet address` },
    { num: 3, text: 'Click "I Have Paid" and submit your TXN ID or screenshot' },
    { num: 4, text: "Minutes added within 10-15 min after admin verification" },
  ];

  return (
    <div data-testid="buy-credits-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-white flex-shrink-0" onClick={() => navigate("/profile")} data-testid="back-to-profile">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>Buy Minutes</h1>
            <p className="text-sm text-slate-500">Purchase analysis minutes via USDT (TRC20) to power AI call analysis</p>
          </div>
        </div>
        <button onClick={() => fetchData()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-2 transition-all" data-testid="refresh-buy-credits">
          <RefreshCw className="h-4 w-4" /><span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Steps Bar — Horizontal */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {steps.map(({ num, text }) => (
          <div key={num} className="flex items-start gap-2.5 bg-slate-900/40 border border-slate-800/40 rounded-xl px-3.5 py-3">
            <div className="h-6 w-6 rounded-full bg-blue-500/15 border border-blue-500/25 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-blue-400">{num}</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">{text}</p>
          </div>
        ))}
      </div>

      {/* Main Content: 3-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* COLUMN 1 — Select Package (spans 2 cols) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Package Selection */}
          {packages.length > 1 && (
            <div>
              <p className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-green-400" /> Select a Package
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {packages.map((pkg, i) => {
                  const isSelected = selectedPackage === i;
                  const hasBonus = pkg.bonus > 0;
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedPackage(i)}
                      className={`relative rounded-xl border p-4 text-left transition-all ${
                        isSelected
                          ? "bg-green-500/10 border-green-500/40 ring-2 ring-green-500/20 shadow-lg shadow-green-500/5"
                          : "bg-slate-900/60 border-slate-800/50 hover:border-slate-600 hover:bg-slate-900/80"
                      }`}
                      data-testid={`package-${i}`}
                    >
                      {hasBonus && (
                        <span className="absolute -top-2.5 right-2 px-2 py-0.5 bg-amber-500 text-black text-[10px] font-bold rounded-full shadow-sm">
                          +{pkg.bonus.toLocaleString()} BONUS
                        </span>
                      )}
                      <p className={`text-[11px] font-semibold uppercase tracking-wider mb-1 ${isSelected ? "text-green-400" : "text-slate-500"}`}>
                        {pkg.label}
                      </p>
                      <p className={`text-2xl font-bold font-mono ${isSelected ? "text-white" : "text-slate-300"}`}>
                        {pkg.minutes.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500">minutes</p>
                      <div className="mt-2 pt-2 border-t border-slate-800/50">
                        <p className={`text-base font-bold font-mono ${isSelected ? "text-green-400" : "text-slate-400"}`}>
                          ${pkg.price_usd}
                        </p>
                        <p className="text-[10px] text-slate-600">${(pkg.price_usd / pkg.minutes).toFixed(4)}/min</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selected Package + I Have Paid */}
          {!showForm ? (
            <div className="flex items-center justify-between bg-gradient-to-r from-green-500/8 to-emerald-500/4 border border-green-500/20 rounded-xl p-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-5 w-5 text-green-400" />
                  <span className="text-lg font-bold text-green-400">{currentPkg.minutes.toLocaleString()} Minutes</span>
                  <span className="text-sm text-slate-500">/ {currentPkg.label} Package</span>
                  {currentPkg.bonus > 0 && <span className="text-xs bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full font-medium">+{currentPkg.bonus.toLocaleString()} bonus</span>}
                </div>
                <p className="text-2xl font-bold text-white font-mono">{currentPkg.price_usd} USDT</p>
              </div>
              <Button
                onClick={() => setShowForm(true)}
                className="h-12 bg-green-600 hover:bg-green-500 text-white font-semibold px-8 text-base shadow-lg shadow-green-600/20"
                data-testid="i-have-paid-btn"
              >
                <CheckCircle2 className="h-5 w-5 mr-2" />
                I Have Paid
              </Button>
            </div>
          ) : (
            <Card className="bg-slate-900/60 border-green-500/20">
              <CardContent className="pt-5 pb-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-base font-semibold text-green-400">Submit Payment Proof</p>
                  <span className="text-sm text-slate-400 font-mono">{currentPkg.price_usd} USDT / {currentPkg.minutes.toLocaleString()} minutes</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-slate-400 text-xs uppercase tracking-wider">Transaction ID (recommended)</Label>
                    <Input
                      value={txnId}
                      onChange={(e) => setTxnId(e.target.value)}
                      placeholder="Enter your USDT TXN hash"
                      className="bg-slate-950/60 border-slate-800/50 text-slate-100 font-mono text-sm"
                      data-testid="txn-id-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-400 text-xs uppercase tracking-wider">Payment Screenshot</Label>
                    <div
                      onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-2 bg-slate-950/60 border border-dashed border-slate-700 rounded-lg px-3 py-2 cursor-pointer hover:border-blue-500/40 transition h-[36px]"
                      data-testid="screenshot-upload-area"
                    >
                      <Upload className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      {screenshot ? (
                        <p className="text-sm text-green-400 truncate flex-1">{screenshot.name}</p>
                      ) : (
                        <p className="text-sm text-slate-500 flex-1">Click to upload (max 10MB)</p>
                      )}
                      {screenshot && (
                        <button onClick={(e) => { e.stopPropagation(); setScreenshot(null); }} className="text-slate-500 hover:text-red-400">
                          <XCircle className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
                  </div>
                </div>
                <div className="flex gap-3 pt-1">
                  <Button
                    onClick={handleSubmitPayment}
                    disabled={submitting || (!txnId.trim() && !screenshot)}
                    className="flex-1 bg-green-600 hover:bg-green-500 h-10"
                    data-testid="submit-payment-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Submit Payment
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowForm(false); setTxnId(""); setScreenshot(null); }}
                    className="text-slate-400 border-slate-700 hover:bg-slate-800 h-10 px-6"
                    data-testid="cancel-payment-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* COLUMN 2 — Deposit Address + History */}
        <div className="space-y-5">
          {/* Deposit Address */}
          <Card className="bg-slate-900/60 border-slate-800/50">
            <CardContent className="pt-5 pb-5 space-y-3">
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-semibold text-slate-200">Deposit Address</span>
              </div>
              <p className="text-xs text-slate-500">Send USDT to this address using the TRC20 network</p>
              <div className="flex items-center gap-2 bg-slate-950/80 border border-slate-800/60 rounded-xl px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mb-1">TRC20 Address</p>
                  <p className="text-sm font-mono text-amber-400 break-all select-all" data-testid="wallet-address">
                    {config?.wallet_address}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0 text-slate-400 hover:text-white hover:bg-slate-800"
                  onClick={handleCopyAddress}
                  data-testid="copy-address-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border bg-red-500/5 border-red-500/15 text-red-300">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] leading-relaxed">
                  Only send <span className="font-semibold text-red-200">USDT</span> on <span className="font-semibold text-red-200">TRC20</span>. Wrong network = permanent loss.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card className="bg-slate-900/60 border-slate-800/50">
            <CardContent className="pt-5 pb-5">
              <p className="text-sm font-semibold text-slate-200 mb-3">Payment History</p>
              {submissions.length > 0 ? (
                <div className="space-y-2">
                  {submissions.map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between bg-slate-950/50 rounded-lg px-3.5 py-2.5 border border-slate-800/30">
                      <div className="space-y-0.5 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-200">{sub.plan_label}</span>
                          <span className="text-xs text-slate-500">({sub.amount_usdt} USDT)</span>
                        </div>
                        {sub.txn_id && <p className="text-[10px] text-slate-500 font-mono truncate max-w-[200px]">TXN: {sub.txn_id}</p>}
                        <p className="text-[10px] text-slate-600">{new Date(sub.created_at).toLocaleString()}</p>
                        {sub.status === "rejected" && sub.reject_reason && <p className="text-[10px] text-red-400">Reason: {sub.reject_reason}</p>}
                      </div>
                      <div className="flex-shrink-0 ml-2">{getStatusBadge(sub.status)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-slate-700" />
                  <p className="text-xs text-slate-500">No payment history yet</p>
                  <p className="text-[10px] text-slate-600 mt-0.5">Transactions will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
